/**
 * Settings Page
 * Self-service account settings including security management
 */
import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useEffect, useState, useCallback } from "react";
import { useQuery, useMutation } from "convex/react";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Button,
  Input,
  Label,
  Skeleton,
  toast,
  AvatarUpload,
  Switch,
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from "@jourfx/ui";
import { authClient, useSession } from "@/lib/auth";
import { api } from "@jourfx/convex/client";
import { MIN_PASSWORD_LENGTH } from "@jourfx/convex/lib/password";
import {
  Eye,
  EyeOff,
  Loader2,
  Monitor,
  Smartphone,
  LogOut,
  Shield,
  CheckCircle2,
  AlertCircle,
  User,
  Bell,
  CalendarDays,
  Palette,
} from "lucide-react";
import { AvailabilityPanel } from "@/components/scheduling/availability/AvailabilityPanel";
import { BuildInfoCard } from "@/components/settings/BuildInfoCard";

export const Route = createFileRoute("/_authenticated/settings")({
  component: SettingsPage,
});

type SettingsTab = "profile" | "notifications" | "availability" | "security" | "display";

type Preferences = {
  locale?: string;
  notifications?: {
    emailReminders?: boolean;
    appointmentChanges?: boolean;
    newAppointments?: boolean;
    waitingRoom?: boolean;
    weeklySummary?: boolean;
  };
  display?: {
    theme?: "light" | "dark" | "system";
    density?: "comfortable" | "compact";
  };
  availability?: {
    dailyMaxAppointments?: number;
    breakMinutes?: number;
  };
};

function SettingsPage() {
  const { t, i18n } = useTranslation("settings");
  const { t: tc } = useTranslation("common");
  const profile = useQuery(api.users.profile.get) as any;
  const updateProfile = useMutation(api.users.profile.update);
  const generateAvatarUploadUrl = useMutation(api.users.avatar.generateUploadUrl);
  const setAvatar = useMutation(api.users.avatar.setAvatar);
  const removeAvatar = useMutation(api.users.avatar.removeAvatar);

  const [activeTab, setActiveTab] = useState<SettingsTab>("profile");
  const [formState, setFormState] = useState({
    salutation: "Herr",
    title: "",
    firstName: "",
    lastName: "",
    phone: "",
    mobilePhone: "",
  });
  const [preferences, setPreferences] = useState<Preferences>({});
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (profile) {
      const [firstName, ...rest] = (profile.name || "").split(" ");
      setFormState({
        salutation: profile.salutation || "Herr",
        title: profile.title || "",
        firstName: profile.firstName || firstName || "",
        lastName: profile.lastName || rest.join(" ") || "",
        phone: profile.phone || "",
        mobilePhone: profile.mobilePhone || "",
      });
      setPreferences(profile.preferences || {});
    }
  }, [profile]);

  const handleSaveProfile = async () => {
    setIsSaving(true);
    try {
      await updateProfile({
        salutation: formState.salutation,
        title: formState.title || undefined,
        firstName: formState.firstName,
        lastName: formState.lastName,
        phone: formState.phone || undefined,
        mobilePhone: formState.mobilePhone || undefined,
      });
      toast({ title: t("profile.saveSuccess"), variant: "default" });
    } finally {
      setIsSaving(false);
    }
  };

  const handleSavePreferences = async () => {
    setIsSaving(true);
    try {
      await updateProfile({ preferences });
      toast({ title: t("preferences.saveSuccess"), variant: "default" });
    } finally {
      setIsSaving(false);
    }
  };

  if (profile === undefined) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-bold tracking-tight">{t("title")}</h1>
        <p className="text-muted-foreground">{t("subtitle")}</p>
      </div>

      <BuildInfoCard />

      <div className="grid gap-6 lg:grid-cols-[240px_1fr]">
        {/* Sidebar */}
        <Card>
          <CardContent className="p-4 space-y-2">
            <SettingsTabButton
              icon={<User className="h-4 w-4" />}
              label={t("tabs.profile")}
              active={activeTab === "profile"}
              onClick={() => setActiveTab("profile")}
            />
            <SettingsTabButton
              icon={<Bell className="h-4 w-4" />}
              label={t("tabs.notifications")}
              active={activeTab === "notifications"}
              onClick={() => setActiveTab("notifications")}
            />
            <SettingsTabButton
              icon={<CalendarDays className="h-4 w-4" />}
              label={t("tabs.availability")}
              active={activeTab === "availability"}
              onClick={() => setActiveTab("availability")}
            />
            <SettingsTabButton
              icon={<Shield className="h-4 w-4" />}
              label={t("tabs.security")}
              active={activeTab === "security"}
              onClick={() => setActiveTab("security")}
            />
            <SettingsTabButton
              icon={<Palette className="h-4 w-4" />}
              label={t("tabs.display")}
              active={activeTab === "display"}
              onClick={() => setActiveTab("display")}
            />
          </CardContent>
        </Card>

        {/* Content */}
        {activeTab === "profile" && (
          <Card>
            <CardHeader>
              <CardTitle>{t("profile.title")}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <AvatarUpload
                currentAvatarUrl={profile?.avatarUrl ?? null}
                userName={profile?.name || "U"}
                generateUploadUrl={() => generateAvatarUploadUrl()}
                onUploadComplete={async (storageId) => {
                  await setAvatar({ storageId: storageId as any });
                  toast({ title: t("profile.avatarSaved"), variant: "default" });
                }}
                onRemove={async () => {
                  await removeAvatar();
                  toast({ title: t("profile.avatarRemoved"), variant: "default" });
                }}
                labels={{
                  upload: t("profile.avatarUpload"),
                  remove: t("profile.avatarRemove"),
                  cropTitle: t("profile.avatarCropTitle"),
                  cropConfirm: t("profile.avatarCropConfirm"),
                  cropCancel: t("profile.avatarCropCancel"),
                }}
              />

              <div className="grid gap-3 md:grid-cols-2">
                <div className="space-y-2">
                  <Label>{t("profile.salutation")}</Label>
                  <Select
                    value={formState.salutation}
                    onValueChange={(value) => setFormState((s) => ({ ...s, salutation: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Herr">{tc("salutations.herr")}</SelectItem>
                      <SelectItem value="Frau">{tc("salutations.frau")}</SelectItem>
                      <SelectItem value="Divers">{tc("salutations.divers")}</SelectItem>
                      <SelectItem value="Dr.">{tc("salutations.dr")}</SelectItem>
                      <SelectItem value="Prof.">{tc("salutations.prof")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>{t("profile.titleField")}</Label>
                  <Input
                    value={formState.title}
                    onChange={(e) => setFormState((s) => ({ ...s, title: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t("profile.firstName")}</Label>
                  <Input
                    value={formState.firstName}
                    onChange={(e) => setFormState((s) => ({ ...s, firstName: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t("profile.lastName")}</Label>
                  <Input
                    value={formState.lastName}
                    onChange={(e) => setFormState((s) => ({ ...s, lastName: e.target.value }))}
                  />
                </div>
              </div>

              <div className="grid gap-3 md:grid-cols-2">
                <div className="space-y-2">
                  <Label>{t("profile.phone")}</Label>
                  <Input
                    value={formState.phone}
                    onChange={(e) => setFormState((s) => ({ ...s, phone: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t("profile.mobile")}</Label>
                  <Input
                    value={formState.mobilePhone}
                    onChange={(e) => setFormState((s) => ({ ...s, mobilePhone: e.target.value }))}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>{t("profile.email")}</Label>
                <Input value={profile.email} disabled />
                <p className="text-xs text-muted-foreground">{t("profile.emailLocked")}</p>
              </div>

              <div className="flex justify-end">
                <Button onClick={handleSaveProfile} disabled={isSaving}>
                  {isSaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                  {t("profile.save")}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {activeTab === "notifications" && (
          <Card>
            <CardHeader>
              <CardTitle>{t("notifications.title")}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <NotificationToggle
                label={t("notifications.reminders")}
                checked={preferences.notifications?.emailReminders ?? true}
                onChange={(checked) =>
                  setPreferences((p) => ({
                    ...p,
                    notifications: { ...p.notifications, emailReminders: checked },
                  }))
                }
              />
              <NotificationToggle
                label={t("notifications.changes")}
                checked={preferences.notifications?.appointmentChanges ?? true}
                onChange={(checked) =>
                  setPreferences((p) => ({
                    ...p,
                    notifications: { ...p.notifications, appointmentChanges: checked },
                  }))
                }
              />
              <NotificationToggle
                label={t("notifications.newAppointments")}
                checked={preferences.notifications?.newAppointments ?? true}
                onChange={(checked) =>
                  setPreferences((p) => ({
                    ...p,
                    notifications: { ...p.notifications, newAppointments: checked },
                  }))
                }
              />
              <NotificationToggle
                label={t("notifications.waitingRoom")}
                checked={preferences.notifications?.waitingRoom ?? true}
                onChange={(checked) =>
                  setPreferences((p) => ({
                    ...p,
                    notifications: { ...p.notifications, waitingRoom: checked },
                  }))
                }
              />
              <NotificationToggle
                label={t("notifications.weekly")}
                checked={preferences.notifications?.weeklySummary ?? false}
                onChange={(checked) =>
                  setPreferences((p) => ({
                    ...p,
                    notifications: { ...p.notifications, weeklySummary: checked },
                  }))
                }
              />
              <div className="flex justify-end">
                <Button onClick={handleSavePreferences} disabled={isSaving}>
                  {isSaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                  {t("notifications.save")}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {activeTab === "availability" && (
          <Card>
            <CardHeader>
              <CardTitle>{t("availability.title")}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-3 md:grid-cols-2">
                <div className="space-y-2">
                  <Label>{t("availability.dailyMax")}</Label>
                  <Input
                    type="number"
                    value={preferences.availability?.dailyMaxAppointments ?? 8}
                    onChange={(e) =>
                      setPreferences((p) => ({
                        ...p,
                        availability: {
                          ...p.availability,
                          dailyMaxAppointments: Number(e.target.value),
                        },
                      }))
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t("availability.breakMinutes")}</Label>
                  <Input
                    type="number"
                    value={preferences.availability?.breakMinutes ?? 10}
                    onChange={(e) =>
                      setPreferences((p) => ({
                        ...p,
                        availability: {
                          ...p.availability,
                          breakMinutes: Number(e.target.value),
                        },
                      }))
                    }
                  />
                </div>
              </div>
              <div className="flex justify-end">
                <Button onClick={handleSavePreferences} disabled={isSaving}>
                  {isSaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                  {t("availability.save")}
                </Button>
              </div>
              <AvailabilityPanel />
            </CardContent>
          </Card>
        )}

        {activeTab === "security" && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5" />
                {t("security.title")}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-8">
              <ChangePasswordSection />
              <div className="border-t" />
              <ActiveSessionsSection />
            </CardContent>
          </Card>
        )}

        {activeTab === "display" && (
          <Card>
            <CardHeader>
              <CardTitle>{t("display.title")}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>{t("display.language")}</Label>
                <Select
                  value={i18n.language}
                  onValueChange={(value) => i18n.changeLanguage(value)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="de">{t("display.languageDe")}</SelectItem>
                    <SelectItem value="en">{t("display.languageEn")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t("display.theme")}</Label>
                <Select
                  value={preferences.display?.theme || "light"}
                  onValueChange={(value) =>
                    setPreferences((p) => ({
                      ...p,
                      display: { ...p.display, theme: value as any },
                    }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="light">{t("display.themeLight")}</SelectItem>
                    <SelectItem value="dark">{t("display.themeDark")}</SelectItem>
                    <SelectItem value="system">{t("display.themeSystem")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t("display.density")}</Label>
                <Select
                  value={preferences.display?.density || "comfortable"}
                  onValueChange={(value) =>
                    setPreferences((p) => ({
                      ...p,
                      display: { ...p.display, density: value as any },
                    }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="comfortable">{t("display.densityComfortable")}</SelectItem>
                    <SelectItem value="compact">{t("display.densityCompact")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex justify-end">
                <Button onClick={handleSavePreferences} disabled={isSaving}>
                  {isSaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                  {t("display.save")}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

function SettingsTabButton({
  icon,
  label,
  active,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={`flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
        active ? "bg-primary text-primary-foreground" : "hover:bg-accent"
      }`}
    >
      {icon}
      <span>{label}</span>
    </button>
  );
}

function NotificationToggle({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <label className="flex items-center justify-between gap-3 rounded-md border p-3 text-sm">
      <span>{label}</span>
      <Switch checked={checked} onCheckedChange={onChange} />
    </label>
  );
}

/**
 * Change Password Form Component
 */
function ChangePasswordSection() {
  const { t } = useTranslation("settings");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setError(null);
      setSuccess(false);

      // Validation
      if (!currentPassword) {
        setError(t("security.changePassword.currentRequired"));
        return;
      }

      if (!newPassword) {
        setError(t("security.changePassword.newRequired"));
        return;
      }

      if (newPassword.length < MIN_PASSWORD_LENGTH) {
        setError(t("security.changePassword.tooShort"));
        return;
      }

      if (newPassword !== confirmPassword) {
        setError(t("security.changePassword.mismatch"));
        return;
      }

      setIsLoading(true);

      try {
        const result = await authClient.changePassword({
          currentPassword,
          newPassword,
        });

        if (result.error) {
          setError(t("security.changePassword.error"));
          return;
        }

        setSuccess(true);
        // Clear form
        setCurrentPassword("");
        setNewPassword("");
        setConfirmPassword("");

        toast({
          title: t("security.changePassword.success"),
          variant: "default",
        });
      } catch {
        setError(t("security.changePassword.error"));
      } finally {
        setIsLoading(false);
      }
    },
    [currentPassword, newPassword, confirmPassword, t]
  );

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-medium">{t("security.changePassword.title")}</h3>
        <p className="text-sm text-muted-foreground">
          {t("security.changePassword.description")}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4 max-w-md">
        {/* Current Password */}
        <div className="space-y-2">
          <Label htmlFor="current-password">{t("security.changePassword.currentPassword")}</Label>
          <div className="relative">
            <Input
              id="current-password"
              type={showCurrentPassword ? "text" : "password"}
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              placeholder="••••••••"
            />
            <button
              type="button"
              onClick={() => setShowCurrentPassword(!showCurrentPassword)}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground"
            >
              {showCurrentPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </div>

        {/* New Password */}
        <div className="space-y-2">
          <Label htmlFor="new-password">{t("security.changePassword.newPassword")}</Label>
          <div className="relative">
            <Input
              id="new-password"
              type={showNewPassword ? "text" : "password"}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="••••••••"
            />
            <button
              type="button"
              onClick={() => setShowNewPassword(!showNewPassword)}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground"
            >
              {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </div>

        {/* Confirm Password */}
        <div className="space-y-2">
          <Label htmlFor="confirm-password">{t("security.changePassword.confirmPassword")}</Label>
          <div className="relative">
            <Input
              id="confirm-password"
              type={showConfirmPassword ? "text" : "password"}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="••••••••"
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground"
            >
              {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </div>

        {error && (
          <div className="flex items-center gap-2 text-sm text-destructive">
            <AlertCircle className="h-4 w-4" />
            {error}
          </div>
        )}

        {success && (
          <div className="flex items-center gap-2 text-sm text-green-600">
            <CheckCircle2 className="h-4 w-4" />
            {t("security.changePassword.success")}
          </div>
        )}

        <Button type="submit" disabled={isLoading}>
          {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {t("security.changePassword.save")}
        </Button>
      </form>
    </div>
  );
}

/**
 * Active Sessions Section
 */
function ActiveSessionsSection() {
  const { t } = useTranslation("settings");
  const { data: session } = useSession();
  const sessions = useQuery(api.auth.sessions.listMySessions) as
    | { id: string; userAgent: string; ipAddress: string; createdAt: number; expiresAt: number }[]
    | undefined;
  const revokeSession = useMutation(api.auth.sessions.revokeMySession);
  const revokeAllOther = useMutation(api.auth.sessions.revokeAllMyOtherSessions);
  const currentSessionId = session?.session?.id;

  if (!sessions) {
    return <Skeleton className="h-32 w-full" />;
  }

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-medium">{t("security.sessions.title")}</h3>
        <p className="text-sm text-muted-foreground">{t("security.sessions.description")}</p>
      </div>

      <div className="space-y-2">
        {sessions.map((session) => (
          <div key={session.id} className="flex items-center justify-between rounded-md border p-3 text-sm">
            <div className="flex items-center gap-2">
              {session.userAgent?.includes("Mobile") ? (
                <Smartphone className="h-4 w-4" />
              ) : (
                <Monitor className="h-4 w-4" />
              )}
              <span>{session.userAgent || t("security.sessions.unknown")}</span>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                if (!currentSessionId) {
                  toast({ title: t("security.sessions.error"), variant: "destructive" });
                  return;
                }
                void revokeSession({ sessionId: session.id, currentSessionId }).catch(() => {
                  toast({ title: t("security.sessions.error"), variant: "destructive" });
                });
              }}
            >
              {t("security.sessions.revoke")}
            </Button>
          </div>
        ))}
      </div>

      <Button
        variant="destructive"
        onClick={() => {
          if (!currentSessionId) {
            toast({ title: t("security.sessions.error"), variant: "destructive" });
            return;
          }
          void revokeAllOther({ currentSessionId }).catch(() => {
            toast({ title: t("security.sessions.error"), variant: "destructive" });
          });
        }}
      >
        <LogOut className="mr-2 h-4 w-4" />
        {t("security.sessions.revokeAll")}
      </Button>
    </div>
  );
}
